// Loudness of a take and gain over a stretch of it — the maths behind the
// loudness_fix recipe and the agent's measure_loudness tool, shared with the
// browser (CommonJS, no Node APIs).
//
// Level = RMS over 50 ms frames, in dBFS. The level of a stretch is the mean
// power of its VOICED frames (frames above the silence floor), so a long
// pause does not make a loud voice look quiet. A gain is applied with linear
// ramps at both ends of the stretch, so a change of level is not heard as a
// click; a gain that would clip is lowered to keep the peak under a ceiling.

const HOP_SEC = 0.05;
const SILENCE_DB = -45;     // a frame below this is a pause, not speech
const CEILING_DB = -0.5;    // the peak a gained stretch may reach
const RAMP_SEC = 0.04;

const toDb = (p) => (p > 0 ? 10 * Math.log10(p) : -Infinity);
const ampOf = (db) => Math.pow(10, db / 20);
const round1 = (x) => Math.round(x * 10) / 10;
const round3 = (x) => Math.round(x * 1000) / 1000;

// Mean power (not dB) per frame of [start, end) seconds. -> Float64Array
function framePowers(samples, sr, start = 0, end = Infinity, hopSec = HOP_SEC) {
  const hop = Math.max(1, Math.round(hopSec * sr));
  const s0 = Math.max(0, Math.floor(start * sr));
  const s1 = Math.min(samples.length, Number.isFinite(end) ? Math.ceil(end * sr) : samples.length);
  const n = Math.max(0, Math.floor((s1 - s0) / hop));
  const out = new Float64Array(n);
  for (let i = 0; i < n; i += 1) {
    const off = s0 + i * hop;
    let acc = 0;
    for (let j = 0; j < hop; j += 1) { const v = samples[off + j]; acc += v * v; }
    out[i] = acc / hop;
  }
  return out;
}

// The level of a stretch: {db, peakDb, frames, voiced, durationSec}. `db` is
// null when nothing in the stretch rises above the silence floor.
function speechLevel(samples, sr, start = 0, end = Infinity, { hopSec = HOP_SEC, silenceDb = SILENCE_DB } = {}) {
  const powers = framePowers(samples, sr, start, end, hopSec);
  const floor = Math.pow(10, silenceDb / 10);
  let acc = 0;
  let voiced = 0;
  for (let i = 0; i < powers.length; i += 1) if (powers[i] > floor) { acc += powers[i]; voiced += 1; }
  const s0 = Math.max(0, Math.floor(start * sr));
  const s1 = Math.min(samples.length, Number.isFinite(end) ? Math.ceil(end * sr) : samples.length);
  let peak = 0;
  for (let i = s0; i < s1; i += 1) { const v = Math.abs(samples[i]); if (v > peak) peak = v; }
  return {
    db: voiced ? round1(toDb(acc / voiced)) : null,
    peakDb: peak > 0 ? round1(20 * Math.log10(peak)) : null,
    frames: powers.length, voiced,
    durationSec: round3(Math.max(0, (s1 - s0) / sr)),
  };
}

// The mean of several levels, in the power domain (two takes at -20 and -26
// dB average to -22.0, not -23: the louder one weighs more). null when none is
// measurable.
function meanDb(dbs) {
  const xs = dbs.filter((x) => Number.isFinite(x));
  if (!xs.length) return null;
  return round1(toDb(xs.reduce((a, x) => a + Math.pow(10, x / 10), 0) / xs.length));
}

// The gain that brings `levelDb` to `refDb`: 0 inside the tolerance, else the
// difference clamped to ±maxDb.
function gainNeeded(levelDb, refDb, { toleranceDb = 2, maxDb = 12 } = {}) {
  if (!Number.isFinite(levelDb) || !Number.isFinite(refDb)) return 0;
  const diff = refDb - levelDb;
  if (Math.abs(diff) <= toleranceDb) return 0;
  return round1(Math.max(-maxDb, Math.min(maxDb, diff)));
}

// The largest gain ≤ gainDb that keeps `peakDb` under the ceiling.
function limitGainForPeak(gainDb, peakDb, ceilingDb = CEILING_DB) {
  if (!Number.isFinite(peakDb) || gainDb <= 0) return gainDb;
  return round1(Math.min(gainDb, ceilingDb - peakDb));
}

// A copy of `samples` with each region's gain applied, ramped over `rampSec`
// at both ends (inside the region). Regions are [{start, end, gainDb}] in
// seconds, non-overlapping. -> {samples, peakDb}
function applyGains(samples, sr, regions, rampSec = RAMP_SEC) {
  const out = Float32Array.from(samples);
  const ramp = Math.max(1, Math.round(rampSec * sr));
  for (const r of regions) {
    if (!r || !Number.isFinite(r.gainDb) || r.gainDb === 0) continue;
    const g = ampOf(r.gainDb);
    const a = Math.max(0, Math.floor(r.start * sr));
    const b = Math.min(out.length, Math.ceil(r.end * sr));
    if (b <= a) continue;
    const rp = Math.min(ramp, Math.floor((b - a) / 2));
    for (let i = a; i < b; i += 1) {
      let f = g;
      if (i < a + rp) f = 1 + (g - 1) * ((i - a) / rp);
      else if (i >= b - rp) f = 1 + (g - 1) * ((b - 1 - i) / rp);
      out[i] = samples[i] * f;
    }
  }
  let peak = 0;
  for (let i = 0; i < out.length; i += 1) { const v = Math.abs(out[i]); if (v > peak) peak = v; }
  return { samples: out, peakDb: peak > 0 ? round1(20 * Math.log10(peak)) : null };
}

module.exports = { HOP_SEC, SILENCE_DB, CEILING_DB, RAMP_SEC, framePowers, speechLevel, meanDb, gainNeeded, limitGainForPeak, applyGains, toDb, ampOf };
